/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * File Upload API Service
 * Migrated from legacy AngularJS: app/api/api.files-upload.js
 *
 * This service handles:
 * - Single file uploads (direct to RCM or via AWS S3)
 * - File version uploads
 * - Acknowledgment document template uploads
 * - AWS S3 presigned URL uploads with progress tracking
 * - File compression/zipping before upload
 */

import { baseApiService } from './base'
import type { FetchOptions } from './base'
import { getAwsS3Config } from './api.settings'
import { getAwsUploadUrl, generateUuid } from './files'
import { uploaderService } from '../uploader'
import { File as FileConstants } from '../../constants/file'
import * as checkmateService from '../checkmate'

// Declare zip.js types (loaded from worker scripts)
declare const zip: {
  workerScripts: {
    deflater?: string[]
    inflater?: string[]
  }
  createWriter: (writer: ZipBlobWriter, onSuccess: (writer: ZipWriter) => void, onError: (error: Error) => void) => void
  BlobWriter: new (mimeType: string) => ZipBlobWriter
  BlobReader: new (blob: Blob) => ZipBlobReader
}

interface ZipBlobWriter {
  // Internal zip.js type
}

interface ZipBlobReader {
  // Internal zip.js type
}

interface ZipWriter {
  add: (
    filename: string,
    reader: ZipBlobReader,
    onEnd: () => void,
    onProgress: (currentIndex: number, totalIndex: number) => void,
    options?: { level?: number }
  ) => void
  close: (callback: (blob: Blob) => void) => void
}

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * E-signature validation object for certified copy operations
 */
export interface ESignatureValidation {
  username?: string
  password?: string
  relayState?: string
  result?: unknown
}

/**
 * Upload result response from the server
 */
export interface UploadResult {
  id?: number
  name?: string
  uuid?: string
  success?: boolean
  error?: string
  [key: string]: unknown
}

/**
 * DTO for file upload operations
 */
export interface UploadFileDTO {
  esignatureCC?: string | null
  isBlinded?: string | null
  [key: string]: string | null | undefined
}

/**
 * DTO for file version upload operations
 */
export interface UploadFileVersionDTO {
  esignatureCC?: string | null
  isBlinded?: string | null
  [key: string]: string | null | undefined
}

/**
 * DTO for acknowledgment document upload
 */
export interface UploadAckDocDTO {
  esignatureCC?: string | null
  isBlinded?: string | null
  [key: string]: string | null | undefined
}

/**
 * Options for S3 upload operations
 */
export interface S3UploadOptions {
  onProgress?: (percentage: number) => void
  abortSignal?: AbortSignal
  compressionLevel?: number
}

/**
 * Result of file upload operation
 */
export interface FileUploadResult {
  success: boolean
  data?: UploadResult
  error?: string
}

/**
 * Zip compression result
 */
export interface ZipResult {
  blob: Blob
  time: number
  isZip?: boolean
}

/**
 * Abort controller interface matching legacy pattern
 */
interface AbortController {
  resolve: () => void
  promise?: Promise<void>
}

/**
 * AWS S3 config response
 */
interface AwsS3Config {
  uploadEnabled: boolean
  [key: string]: unknown
}

// ============================================================================
// Constants
// ============================================================================

const BASE_URL = '/api/files/'

// Default compression level (can be overridden via configuration)
const DEFAULT_COMPRESSION_LEVEL = 0

// Template system folder ID for acknowledgment docs
const TEMPLATE_SYSTEM_FOLDER_ID = 3
const EOS_ACK_DOC_TEMPLATE_ID = 4

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a FormData object from configuration
 * Mirrors legacy configureUploadDetails function
 */
function configureUploadDetails(config: Record<string, unknown>): FormData {
  const formData = new FormData()

  Object.entries(config).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (value instanceof Blob) {
        formData.append(key, value)
      } else {
        formData.append(key, String(value))
      }
    }
  })

  return formData
}

/**
 * Creates a throttled progress updater function
 * Calculates total progress across multiple phases
 */
function createThrottledProgressUpdater(
  uploadId: number | undefined,
  throttleMs: number = 500
): (percentage: number, phaseStart?: number, phaseEnd?: number) => void {
  let lastUpdate = 0

  return (percentage: number, phaseStart?: number, phaseEnd?: number) => {
    const now = Date.now()
    if (now - lastUpdate < throttleMs && percentage < 100) {
      return
    }
    lastUpdate = now

    let totalProgress: number
    if (typeof phaseStart === 'number' && typeof phaseEnd === 'number') {
      const currentPhaseLength = phaseEnd - phaseStart
      const totalLength = (100 / currentPhaseLength) * (100 - currentPhaseLength) + 100
      const phaseProgress = (percentage / totalLength) * 100
      totalProgress = phaseStart + phaseProgress
    } else {
      totalProgress = percentage
    }

    if (uploadId !== undefined) {
      uploaderService.setProgress(uploadId, Math.min(100, Math.max(0, totalProgress)))
    }
  }
}

/**
 * Upload file to AWS S3 using presigned URL
 * Uses XMLHttpRequest for progress tracking (fetch doesn't support upload progress)
 */
async function uploadToS3WithPresignedUrl(
  presignedUrl: string,
  file: Blob,
  options: {
    onProgress?: (percentage: number) => void
    abortSignal?: AbortSignal
  } = {}
): Promise<void> {
  const { onProgress, abortSignal } = options

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Handle abort signal
    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        xhr.abort()
        reject(new DOMException('Upload aborted', 'AbortError'))
      })
    }

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress((event.loaded / event.total) * 100)
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('AWS S3 upload success')
        resolve()
      } else {
        console.log('AWS S3 upload failed with status:', xhr.status)
        reject(new Error(`S3 upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      console.log('AWS S3 upload failed')
      reject(new Error('S3 upload failed'))
    }

    // Extract origin and path from presigned URL
    const origin = presignedUrl.split('/', 3).join('/')
    const path = presignedUrl.substring(origin.length)

    xhr.open('PUT', presignedUrl)
    // Don't set Content-Type for presigned URL - it's part of the signature
    xhr.withCredentials = false
    xhr.send(file)
  })
}

/**
 * Configures zip.js worker scripts based on the environment
 * Mirrors legacy worker script configuration with checkmate support
 */
function configureZipWorkerScripts(): void {
  // Check if zip.js is available globally
  if (typeof zip === 'undefined') {
    console.warn('zip.js not available - compression will be skipped')
    return
  }

  // Get configuration from global window object (set by index.html or config)
  // const globalWindow = window as any
  // const config = globalWindow.config || globalWindow.APP_CONFIG || {}

  const buildId = document.documentElement.getAttribute('build-id')
  const cdnUrl = document.documentElement.getAttribute('cdn-url')
  const apiUrl = document.documentElement.getAttribute('api-url') || ''
  const uiUrl = document.documentElement.getAttribute('ui-url') || window.location.origin

  // Extract context path from API URL (e.g., 'edge-rcm' from '/apps/edge-rcm/be')
  let contextPathUriName = ''
  if (apiUrl) {
    const lastIndex = apiUrl.lastIndexOf('/')
    const secondLastIndex = apiUrl.lastIndexOf('/', lastIndex - 1)
    if (secondLastIndex >= 0 && lastIndex > secondLastIndex) {
      contextPathUriName = apiUrl.substring(secondLastIndex + 1, lastIndex)
    }
  }

  if (buildId) {
    // Production mode with build ID
    if (checkmateService.isCheckmated()) {
      // Checkmated environment - include c_selections in worker URL
      // Use parseClientDivisionSchemeId() to match legacy behavior exactly
      const cdsValue = checkmateService.parseClientDivisionSchemeId()
      const workerBaseUrl =
        location.origin + (cdnUrl ? '/apps/' + contextPathUriName + '/app' : '/apps/' + contextPathUriName)
      const workerUrl =
        workerBaseUrl + '/zip-worker-2020-1.js?c_selections=' + checkmateService.CSELECTION_PARAMS.cds + ':' + cdsValue

      zip.workerScripts = {
        deflater: [workerUrl]
      }
    } else {
      // Non-checkmated production environment
      const workerBaseUrl = cdnUrl ? location.origin : uiUrl
      zip.workerScripts = {
        deflater: [workerBaseUrl + '/zip-worker-2020-1.js']
      }
    }
  } else {
    // Development mode - use combined worker file served by Vite (matches Grunt pattern)
    // Vite plugin serves /zip-worker-2020-1.js by combining z-worker.js + deflate.js
    zip.workerScripts = {
      deflater: ['/zip-worker-2020-1.js']
    }
  }
}

/**
 * Formats file size in human-readable format
 * Similar to legacy $filter('bytes') functionality
 */
function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Zips files asynchronously with progress tracking
 * Fully migrated from legacy AngularJS implementation
 *
 * @param files - Files to compress
 * @param updateProgress - Progress callback (0-100)
 * @returns Promise resolving to zip result
 */
export async function zipFilesAsync(files: File[], updateProgress?: (percentage: number) => void): Promise<ZipResult> {
  // If only one file and it's already a ZIP, return as-is
  if (
    files &&
    files.length === 1 &&
    (files[0].type === 'application/zip' || files[0].type === 'application/x-zip-compressed')
  ) {
    return {
      blob: files[0],
      time: 0,
      isZip: false
    }
  }

  // Check if zip.js is available
  if (typeof zip === 'undefined') {
    console.warn('zip.js not available - returning first file without compression')
    if (updateProgress) {
      updateProgress(100)
    }
    return {
      blob: files[0],
      time: 0,
      isZip: false
    }
  }

  // Configure worker scripts based on environment
  configureZipWorkerScripts()

  const timeStart = Date.now()
  let totalSize = 0

  // Get compression level from constants
  const compressionLevel = FileConstants.upload?.compressionLevel ?? DEFAULT_COMPRESSION_LEVEL
  const options = { level: compressionLevel }

  return new Promise((resolve, reject) => {
    zip.createWriter(
      new zip.BlobWriter('application/zip'),
      (writer) => {
        console.log('~upload Files number:', files.length)
        let fileIndex = 0

        const compressFile = (file: File) => {
          writer.add(
            file.name,
            new zip.BlobReader(file),
            () => {
              // File added successfully
              totalSize += file.size
              fileIndex++

              if (fileIndex > files.length - 1) {
                // All files added, close the writer
                writer.close((zippedFilesBlob: Blob) => {
                  const timeDuration = Date.now() - timeStart

                  // Log compression statistics (matching legacy behavior)
                  console.log('~upload Files size:', formatBytes(totalSize))
                  console.log('~upload ZIP compression:', compressionLevel)
                  console.log('~upload ZIP size:', formatBytes(zippedFilesBlob.size))
                  console.log('~upload ZIP duration:', Math.floor(timeDuration / 10) / 100)

                  resolve({
                    isZip: true,
                    blob: zippedFilesBlob,
                    time: timeDuration
                  })
                })
              } else {
                // More files to add
                compressFile(files[fileIndex])
              }
            },
            (currentIndex: number, totalIndex: number) => {
              // Progress callback during compression
              if (updateProgress) {
                updateProgress((currentIndex * 100) / totalIndex)
              }
            },
            options
          )
        }

        // Start compressing files
        compressFile(files[fileIndex])
      },
      (error: Error) => {
        console.error('zipjs init failed', error)
        reject(error)
      }
    )
  })
}

// ============================================================================
// Core Upload Functions
// ============================================================================

/**
 * Uploads files using AWS S3 presigned URL
 *
 * @param files - File(s) to upload
 * @param dontZip - If true, skip compression
 * @param abortSignal - Optional abort signal
 * @param uploadId - Upload ID for progress tracking
 * @returns Promise resolving to UUID of uploaded file
 */
export async function uploadFilesUsingAwsS3(
  files: File | File[],
  dontZip: boolean = false,
  abortSignal?: AbortSignal,
  uploadId?: number
): Promise<string> {
  let uploadAborted = false

  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      uploadAborted = true
    })
  }

  const updateProgress = createThrottledProgressUpdater(uploadId)

  // Convert single file to array
  const fileArray = Array.isArray(files) ? files : [files]

  // Phase 1: File preparation/compression
  let fileData: ZipResult
  if (dontZip) {
    fileData = {
      blob: fileArray[0],
      time: 0
    }
  } else {
    fileData = await zipFilesAsync(fileArray, (percentage) => {
      updateProgress(percentage, 0, 60)
    })
  }

  if (uploadAborted) {
    throw new DOMException('Upload aborted', 'AbortError')
  }

  // Phase 2: Get presigned URL and upload to S3
  const uuid = generateUuid()
  const presignedUrlResponse = await getAwsUploadUrl(uuid)

  if (uploadAborted) {
    throw new DOMException('Upload aborted', 'AbortError')
  }

  const timeStart = Date.now()

  await uploadToS3WithPresignedUrl(presignedUrlResponse.url, fileData.blob, {
    onProgress: (percentage) => {
      updateProgress(percentage, dontZip ? 0 : 60, 100)
    },
    abortSignal
  })

  const timeDuration = Date.now() - timeStart
  console.log('~upload Upload duration:', Math.floor(timeDuration / 10) / 100)

  if (fileData.isZip) {
    console.log('~upload Combined duration:', Math.floor((timeDuration + fileData.time) / 10) / 100)
  }

  return uuid
}

/**
 * Shared upload logic for both direct and S3 uploads
 * Mirrors legacy uploadShared function
 */
async function uploadShared(
  file: File,
  urls: { rcm: string; rcmS3: string },
  formData: Record<string, unknown>,
  queryParams?: Record<string, string | number>
): Promise<UploadResult> {
  // Create abort controller for upload cancellation
  const abortController = new AbortController()
  const abortSignal = abortController.signal

  // Create custom abort object matching legacy pattern
  const abort: AbortController = {
    resolve: () => abortController.abort()
  }

  // Start upload tracking
  const uploadId = uploaderService.startUpload([file], abort, {
    promise: Promise.resolve() // Placeholder, updated below
  })

  try {
    // Check if AWS S3 upload is enabled
    const awsConfig = (await getAwsS3Config()) as AwsS3Config

    if (awsConfig.uploadEnabled) {
      // S3 upload path
      const uuid = await uploadFilesUsingAwsS3(file, true, abortSignal, uploadId)

      // Submit file reference to RCM
      return await submitFilesToRcm(urls.rcmS3, uuid, file.name, formData, queryParams)
    } else {
      // Direct upload path
      return await uploadFilesToRcm(urls.rcm, formData, file, queryParams, uploadId, abortSignal)
    }
  } catch (error) {
    // If AWS config fetch fails, fallback to direct upload
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      console.warn('AWS S3 config check failed, falling back to direct upload:', error)
      return await uploadFilesToRcm(urls.rcm, formData, file, queryParams, uploadId, abortSignal)
    }
    throw error
  }
}

/**
 * Upload files directly to RCM (non-S3 path)
 */
async function uploadFilesToRcm(
  url: string,
  formData: Record<string, unknown>,
  file?: File,
  queryParams?: Record<string, string | number>,
  uploadId?: number,
  abortSignal?: AbortSignal
): Promise<UploadResult> {
  const uploadData = { ...formData }
  if (file) {
    uploadData.data = file
  }

  const fd = configureUploadDetails(uploadData)

  // Build query string
  let queryString = ''
  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      params.append(key, String(value))
    })
    queryString = '?' + params.toString()
  }

  let apiUrl: string | null = null
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '0.0.0.0' ||
      import.meta.env.DEV)
  ) {
    // Local dev: use Vite env or fallback
    apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5173/noApiDefined'
  } else {
    // Production: use attribute from DOM
    apiUrl = document.documentElement.getAttribute('api-url')
  }

  if (!apiUrl) {
    throw new Error('api-url attribute not found for Direct RCM validation')
  }

  const options: FetchOptions = {
    method: 'POST',
    body: fd,
    signal: abortSignal,
    baseUrl: apiUrl,
    disableCSelections: true,
    directConnection: true
  }

  // Note: Progress tracking with fetch is limited
  // For full progress support, we would need XMLHttpRequest
  if (uploadId !== undefined) {
    // Simulate progress for now
    uploaderService.setProgress(uploadId, 50)
  }

  const result = await baseApiService.fetch<UploadResult>(`${BASE_URL}${url}${queryString}`, options)

  if (uploadId !== undefined) {
    uploaderService.setProgress(uploadId, 100)
  }

  return result
}

/**
 * Submit S3 uploaded file reference to RCM
 */
async function submitFilesToRcm(
  url: string,
  s3ObjectName: string,
  fileName: string,
  formData: Record<string, unknown>,
  queryParams?: Record<string, string | number>
): Promise<UploadResult> {
  const uploadData = {
    ...formData,
    fileName,
    s3ObjectName
  }

  const fd = configureUploadDetails(uploadData)

  // Build query string
  let queryString = ''
  if (queryParams && Object.keys(queryParams).length > 0) {
    const params = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      params.append(key, String(value))
    })
    queryString = '?' + params.toString()
  }

  let apiUrl: string | null = null
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '0.0.0.0' ||
      import.meta.env.DEV)
  ) {
    // Local dev: use Vite env or fallback
    apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5173/noApiDefined'
  } else {
    // Production: use attribute from DOM
    apiUrl = document.documentElement.getAttribute('api-url')
  }

  if (!apiUrl) {
    throw new Error('api-url attribute not found for Direct RCM validation')
  }

  const options: FetchOptions = {
    method: 'POST',
    body: fd,
    baseUrl: apiUrl,
    disableCSelections: true,
    directConnection: true
  }

  return baseApiService.fetch<UploadResult>(`${BASE_URL}${url}${queryString}`, options)
}

// ============================================================================
// Public API Functions
// ============================================================================

/**
 * Upload a single file to a parent folder
 *
 * @param file - File to upload
 * @param parentId - Parent folder ID
 * @param validationCC - E-signature validation for certified copy
 * @param isTriggerChangeBlindingStatus - Whether to trigger blinding status change
 * @param isTriggerChangeCertifiedCopy - Whether this is a certified copy change
 * @returns Promise resolving to upload result
 *
 * @example
 * ```typescript
 * const result = await uploadFile(myFile, 123);
 * // With e-signature for certified copy
 * const result = await uploadFile(myFile, 123, validation, false, true);
 * ```
 */
export async function uploadFile(
  file: File,
  parentId: number,
  validationCC?: ESignatureValidation | null,
  isTriggerChangeBlindingStatus?: boolean,
  isTriggerChangeCertifiedCopy?: boolean
): Promise<UploadResult> {
  const uploadFileDTO: UploadFileDTO = {
    esignatureCC: validationCC && isTriggerChangeCertifiedCopy ? JSON.stringify(validationCC) : null,
    isBlinded: isTriggerChangeBlindingStatus ? 'false' : null
  }

  return uploadShared(
    file,
    {
      rcm: 'singleFileUpload',
      rcmS3: 'singleFileUpload/s3upload'
    },
    uploadFileDTO,
    {
      parent: parentId
    }
  )
}

/**
 * Upload a new version of an existing file
 *
 * @param file - File to upload as new version
 * @param fileId - ID of the file to create version for
 * @param validationCC - E-signature validation for certified copy
 * @param isTriggerChangeBlindingStatus - Whether to trigger blinding status change
 * @param isTriggerChangeCertifiedCopy - Whether this is a certified copy change
 * @returns Promise resolving to upload result
 *
 * @example
 * ```typescript
 * const result = await uploadFileVersion(newVersionFile, 456);
 * ```
 */
export async function uploadFileVersion(
  file: File,
  fileId: number,
  validationCC?: ESignatureValidation | null,
  isTriggerChangeBlindingStatus?: boolean,
  isTriggerChangeCertifiedCopy?: boolean
): Promise<UploadResult> {
  const uploadFileVersionDTO: UploadFileVersionDTO = {
    esignatureCC: validationCC && isTriggerChangeCertifiedCopy ? JSON.stringify(validationCC) : null,
    isBlinded: isTriggerChangeBlindingStatus ? 'false' : null
  }

  return uploadShared(
    file,
    {
      rcm: `${fileId}/uploadVersion`,
      rcmS3: `${fileId}/uploadVersion/s3upload`
    },
    uploadFileVersionDTO
  )
}

/**
 * Upload acknowledgment document template
 *
 * @param file - Template file to upload
 * @param parentId - Parent folder ID (defaults to TEMPLATE_SYSTEM_FOLDER_ID)
 * @param documentTypeTemplateId - Document type template ID (defaults to EOS_ACK_DOC_TEMPLATE_ID)
 * @returns Promise resolving to upload result
 *
 * @example
 * ```typescript
 * const result = await uploadAckDocTemplate(templateFile);
 * // With custom IDs
 * const result = await uploadAckDocTemplate(templateFile, customParentId, customDocTypeId);
 * ```
 */
export async function uploadAckDocTemplate(
  file: File,
  isAckDocTemplateUploadFlow?: boolean,
  parentId?: number,
  documentTypeTemplateId?: number
): Promise<UploadResult> {
  const uploadFileDTO: UploadAckDocDTO = {
    esignatureCC: null,
    isBlinded: null
  }

  const fd = configureUploadDetails({
    ...uploadFileDTO,
    file
  })

  const queryParams = {
    parentId: parentId ?? TEMPLATE_SYSTEM_FOLDER_ID,
    documentTypeTemplateId: documentTypeTemplateId ?? EOS_ACK_DOC_TEMPLATE_ID
  }

  // Build query string
  const params = new URLSearchParams()
  Object.entries(queryParams).forEach(([key, value]) => {
    params.append(key, String(value))
  })

  const disableCSelections = !(isAckDocTemplateUploadFlow && checkmateService.isCheckmated())

  const options: FetchOptions = {
    method: 'POST',
    body: fd,
    directConnection: disableCSelections
  }

  return baseApiService.fetch<UploadResult>(`${BASE_URL}uploadAckDocTemplate?${params.toString()}`, options)
}

// ============================================================================
// Legacy-Compatible Export
// ============================================================================

/**
 * Legacy-compatible API object
 * Provides the same interface as the AngularJS service
 */
export const filesUploadApi = {
  uploadFile,
  uploadFileVersion,
  uploadFilesUsingAwsS3,
  uploadAckDocTemplate
}

/**
 * Default export matching legacy factory pattern
 */
export default filesUploadApi
